/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { PhotoDropZone } from '@/Client/Widgets/PhotoDropZone';
import { styleguideFontFamily as FONT, withOpacity } from '@kaiizen/kai-shiki/core';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { type GalleryPhotoOccurrence, getGalleryPhotoOccurrenceKey } from '../galleryPhotoOperations';
import type { PhotoManagerSection } from '../photoManagerModel';
import { getPhotoCountLabel } from './photoManagerLib';
import { SECTION_COUNT_STYLE } from './photoManagerStyles';
import { PhotoTile } from './PhotoTile';
import { SectionUpload } from './SectionUpload';

/* No :last-child zeroing here: photo-accepting sections render wrapped in their own
   PhotoDropZone div, which makes every one of them its parent's last child — the rule
   would zero the gap on exactly the sections that carry photos. The trailing margin is
   absorbed by the body's 90px bottom padding instead. Vacant sections keep the same 30px:
   they hold a tile the same size as a photo now, so the section is the same shape as any
   other and takes the same rhythm. */
const SECTION_STYLE = css`
  label: PHOTO_MANAGER_SECTION;
  margin-bottom: 30px;
`;

const SECTION_HEADER_STYLE = css`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const GROUP_SECTION_NAME_STYLE = css`
  font-family: ${FONT.mono};
  font-size: ${pxToRem(12)};
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const TASK_SECTION_NAME_STYLE = css`
  font-family: ${FONT.display};
  font-size: ${pxToRem(15)};
  font-weight: 700;
  color: ${withOpacity(COLORS.white, 0.88)};
`;

/**
 * Mockup `.pm-sec-grouplbl`: highlighter, not an underline — the lemon sits behind the whole
 * label so a group heading reads as a marked line. Text flips to ink inside it; white on
 * lemon is unreadable, and this is the one place in the dark takeover where the ground goes
 * light. Curbside and Unsorted take the label without the GROUP tag.
 */

const GROUP_LABEL_STYLE = css`
  label: PHOTO_MANAGER_GROUP_LABEL;
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  background: ${COLORS.accent};
  padding: 3px 8px;

  & > span {
    color: ${COLORS.ink};
  }
`;

const GROUP_KIND_TAG_STYLE = css`
  font-family: ${FONT.mono};
  font-size: ${pxToRem(12)};
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

/* One size for every section. The mockup gives group-level sections a 128px track against a task's
   170px (`.pm-sec.is-group .pm-grid`), which put unsorted, curbside and group tiles a step below
   the task tiles beside them; group acceptance testing asked for the larger size throughout. */
const PHOTO_GRID_STYLE = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 16px 12px;
`;

const EMPTY_NOTE_STYLE = css`
  label: PHOTO_MANAGER_EMPTY_NOTE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(12.5)};
  color: ${withOpacity(COLORS.white, 0.42)};
  padding: 8px 2px;
`;

export const PhotoManagerSectionView = ({
  section,
  scanIsEditable,
  selectedOccurrences,
  onToggleSelection,
  onOpenPhoto,
}: {
  section: PhotoManagerSection;
  scanIsEditable: boolean;
  selectedOccurrences: ReadonlyMap<string, GalleryPhotoOccurrence>;
  onToggleSelection: (occurrence: GalleryPhotoOccurrence) => void;
  onOpenPhoto: (occurrence: GalleryPhotoOccurrence) => void;
}) => {
  const canAdd = scanIsEditable && section.acceptsPhotos;
  const content = (
    <section css={SECTION_STYLE} data-testid={`photo-manager-section-${section.key}`}>
      <div css={SECTION_HEADER_STYLE}>
        {section.kind === 'task'
          ? <span css={TASK_SECTION_NAME_STYLE}>{section.title}</span>
          : (
            <span css={GROUP_LABEL_STYLE} data-testid={section.kind === 'group' ? 'photo-manager-group-label' : undefined}>
              <span css={GROUP_SECTION_NAME_STYLE}>{section.title}</span>
              {section.kind === 'group' && <span css={GROUP_KIND_TAG_STYLE}>Group</span>}
            </span>
          )}
        <span css={SECTION_COUNT_STYLE}>{getPhotoCountLabel(section.photos.length)}</span>
      </div>
      {section.photos.length || canAdd
        ? (
          <div css={PHOTO_GRID_STYLE}>
            {section.photos.map((entry, index) => (
              <PhotoTile
                index={index}
                key={`${entry.photoId}-${index}`}
                onToggleSelection={onToggleSelection}
                onOpenPhoto={onOpenPhoto}
                photo={entry}
                scanIsEditable={scanIsEditable}
                section={section}
                selected={selectedOccurrences.has(
                  getGalleryPhotoOccurrenceKey({ ...section.scanPartContext, photoId: entry.photoId }),
                )}
              />
            ))}
            {canAdd && <SectionUpload section={section} />}
          </div>
        )
        : (
          <div css={EMPTY_NOTE_STYLE} data-testid='photo-manager-empty-note'>
            No photos here yet.
          </div>
        )}
    </section>
  );
  // The drop zone wraps the section so a drop anywhere on it — header, grid, strip — files
  // into that section. Curbside (acceptsPhotos false) stays display-only.
  return canAdd
    ? (
      <PhotoDropZone scanPart={section.scanPart} scanPartContext={section.scanPartContext}>
        {content}
      </PhotoDropZone>
    )
    : content;
};
